import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Eye, Send, Mail, Plus, Pencil, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";
import AliancaCard from "./AliancaCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { draft: "Rascunho", active: "Ativa", archived: "Arquivada" };
const STATUS_STYLE = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-gray-100 text-gray-500 border-gray-200",
};

const EMPTY_DAY = { day: 1, subject: "", quote: "", quote_source: "Documento de Fundação", impulse: "", reflection: "" };
const EMPTY_FORM = {
  title: "", subtitle: "", start_date: "", send_hour: "17:00",
  status: "draft", intro: "", final_prayer: "", days: [{ ...EMPTY_DAY }],
};

function DayEditor({ day, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...day, [k]: v });
  return (
    <div className="border border-[hsl(var(--border))] p-4 space-y-3 bg-[hsl(var(--background))]">
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">Dia {day.day}</span>
        <input value={day.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Tema do dia"
          className="flex-1 border border-[hsl(var(--border))] px-2 py-1.5 text-sm bg-white" data-testid={`novena-day-subject-${day.day}`} />
        <button onClick={onRemove} className="p-1.5 text-[hsl(var(--muted-foreground))] hover:text-red-600" title="Remover dia">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <textarea value={day.quote} onChange={(e) => set("quote", e.target.value)} rows={2} placeholder="Citação"
        className="w-full border border-[hsl(var(--border))] px-2 py-1.5 text-sm bg-white" />
      <input value={day.quote_source} onChange={(e) => set("quote_source", e.target.value)} placeholder="Fonte da citação"
        className="w-full border border-[hsl(var(--border))] px-2 py-1.5 text-sm bg-white" />
      <textarea value={day.impulse} onChange={(e) => set("impulse", e.target.value)} rows={5}
        placeholder="Impulso para a vida (parágrafos separados por linha em branco)"
        className="w-full border border-[hsl(var(--border))] px-2 py-1.5 text-sm bg-white" />
      <textarea value={day.reflection} onChange={(e) => set("reflection", e.target.value)} rows={3}
        placeholder="Reflexão / perguntas"
        className="w-full border border-[hsl(var(--border))] px-2 py-1.5 text-sm bg-white" />
    </div>
  );
}

function NovenaForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setDay = (i, d) => setForm((f) => ({ ...f, days: f.days.map((x, j) => (j === i ? d : x)) }));
  const addDay = () => setForm((f) => ({ ...f, days: [...f.days, { ...EMPTY_DAY, day: f.days.length + 1 }] }));
  const removeDay = (i) => setForm((f) => ({
    ...f,
    days: f.days.filter((_, j) => j !== i).map((d, j) => ({ ...d, day: j + 1 })),
  }));

  const save = async () => {
    if (!form.title.trim() || !form.start_date) { toast.error("Título e data de início são obrigatórios."); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.put(`${API}/admin/novenas/${initial.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/novenas`, form, { withCredentials: true });
      }
      toast.success("Novena guardada.");
      onSaved();
    } catch (e) {
      console.error("[Novenas] save:", e?.message);
      toast.error(e?.response?.data?.detail || "Erro a guardar a novena.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="novena-form-modal">
      <div className="bg-white w-full max-w-3xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[hsl(var(--primary))]">
          {initial?.id ? "Editar novena" : "Nova novena"}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Título"
            className="border border-[hsl(var(--border))] px-3 py-2 text-sm sm:col-span-2" data-testid="novena-title-input" />
          <input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtítulo / lema"
            className="border border-[hsl(var(--border))] px-3 py-2 text-sm sm:col-span-2" />
          <label className="text-xs text-[hsl(var(--muted-foreground))]">Data de início (dia 1)
            <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)}
              className="mt-1 w-full border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="novena-start-date-input" />
          </label>
          <label className="text-xs text-[hsl(var(--muted-foreground))]">Estado
            <select value={form.status} onChange={(e) => set("status", e.target.value)}
              className="mt-1 w-full border border-[hsl(var(--border))] px-3 py-2 text-sm bg-white" data-testid="novena-status-select">
              <option value="draft">Rascunho</option>
              <option value="active">Ativa (envia às 17h00)</option>
              <option value="archived">Arquivada</option>
            </select>
          </label>
        </div>
        <textarea value={form.final_prayer} onChange={(e) => set("final_prayer", e.target.value)} rows={5}
          placeholder="Oração final diária (incluída em todos os emails)"
          className="w-full border border-[hsl(var(--border))] px-3 py-2 text-sm" />
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Dias ({form.days.length})</span>
            <button onClick={addDay} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline" data-testid="novena-add-day-btn">
              <Plus className="h-3.5 w-3.5" /> Adicionar dia
            </button>
          </div>
          {form.days.map((d, i) => (
            <DayEditor key={i} day={d} onChange={(nd) => setDay(i, nd)} onRemove={() => removeDay(i)} />
          ))}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[hsl(var(--border))]" data-testid="novena-form-cancel">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 text-sm bg-[hsl(var(--primary))] text-white disabled:opacity-50" data-testid="novena-form-save">
            {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NovenaCard({ novena, recipientsCount, onChanged, onEdit }) {
  const [open, setOpen] = useState(novena.status === "active");
  const [testEmail, setTestEmail] = useState("");

  const preview = (dayNum) => {
    window.open(`${API}/admin/novenas/${novena.id}/preview/${dayNum}`, "_blank");
  };

  const sendNow = async (dayNum) => {
    if (!window.confirm(`Enviar o ${dayNum}.º dia a ${recipientsCount} sócio(s) agora?`)) return;
    try {
      await axios.post(`${API}/admin/novenas/${novena.id}/send-day/${dayNum}`, {}, { withCredentials: true });
      toast.success(`${dayNum}.º dia em envio para ${recipientsCount} sócio(s).`);
      setTimeout(onChanged, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio.");
    }
  };

  const sendTest = async (dayNum) => {
    if (!testEmail.trim()) { toast.error("Indique o email de teste."); return; }
    try {
      await axios.post(`${API}/admin/novenas/${novena.id}/test-day/${dayNum}`,
        { email: testEmail.trim() }, { withCredentials: true });
      toast.success(`Teste do ${dayNum}.º dia enviado para ${testEmail.trim()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio de teste.");
    }
  };

  const remove = async () => {
    if (!window.confirm("Apagar esta novena e o seu histórico de envios?")) return;
    try {
      await axios.delete(`${API}/admin/novenas/${novena.id}`, { withCredentials: true });
      toast.success("Novena apagada.");
      onChanged();
    } catch { toast.error("Erro a apagar."); }
  };

  const sentDays = novena.sent_days || {};

  return (
    <div className="bg-white border border-[hsl(var(--border))]" data-testid={`novena-card-${novena.id}`}>
      <div className="p-5 flex flex-wrap items-center gap-3">
        <Sparkles className="h-5 w-5 text-[#B08D1E] shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold text-[hsl(var(--foreground))]">{novena.title}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            Início {novena.start_date} · {novena.days?.length || 0} dias · envio diário às {novena.send_hour}
            {novena.current_day ? ` · hoje é o ${novena.current_day}.º dia` : ""}
          </div>
        </div>
        <span className={`text-[11px] px-2.5 py-1 border uppercase tracking-wider ${STATUS_STYLE[novena.status]}`} data-testid={`novena-status-${novena.id}`}>
          {STATUS_LABEL[novena.status]}
        </span>
        <button onClick={() => onEdit(novena)} className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]" title="Editar" data-testid={`novena-edit-${novena.id}`}>
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={remove} className="p-2 border border-[hsl(var(--border))] hover:border-red-400 hover:text-red-600" title="Apagar" data-testid={`novena-delete-${novena.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
        <button onClick={() => setOpen(!open)} className="p-2 border border-[hsl(var(--border))]" data-testid={`novena-toggle-${novena.id}`}>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[hsl(var(--border))] p-5">
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-[hsl(var(--muted-foreground))]">
            <Mail className="h-3.5 w-3.5" />
            Teste para:
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email@exemplo.pt"
              className="border border-[hsl(var(--border))] px-2 py-1 text-xs w-56" data-testid={`novena-test-email-${novena.id}`} />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="py-2 pr-2">Dia</th>
                <th className="py-2 pr-2">Tema</th>
                <th className="py-2 pr-2">Estado de envio</th>
                <th className="py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {(novena.days || []).map((d) => {
                const log = sentDays[String(d.day)];
                const isToday = novena.current_day === d.day;
                return (
                  <tr key={d.day} className={`border-b border-[hsl(var(--border))]/60 ${isToday ? "bg-[#FBF6E8]" : ""}`}>
                    <td className="py-2.5 pr-2 font-semibold text-[hsl(var(--primary))]">{d.day}.º{isToday ? " · hoje" : ""}</td>
                    <td className="py-2.5 pr-2">{d.subject}</td>
                    <td className="py-2.5 pr-2">
                      {log ? (
                        <span className="text-emerald-700" data-testid={`novena-sent-${novena.id}-${d.day}`}>
                          Enviado a {log.sent_count} ({log.trigger === "cron" ? "automático" : "manual"})
                        </span>
                      ) : (
                        <span className="text-[hsl(var(--muted-foreground))]">Por enviar</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => preview(d.day)} title="Pré-visualizar email"
                        className="p-1.5 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] mr-1.5"
                        data-testid={`novena-preview-${novena.id}-${d.day}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => sendTest(d.day)} title="Enviar teste para o email acima"
                        className="p-1.5 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] mr-1.5"
                        data-testid={`novena-send-test-${novena.id}-${d.day}`}>
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => sendNow(d.day)} disabled={!!log}
                        title={log ? "Já enviado" : "Enviar agora a todos os sócios"}
                        className="p-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white disabled:opacity-30 disabled:pointer-events-none"
                        data-testid={`novena-send-now-${novena.id}-${d.day}`}>
                        <Send className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminNovenas() {
  const [items, setItems] = useState([]);
  const [recipients, setRecipients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (novo) | novena

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/novenas`, { withCredentials: true });
      setItems(r.data?.items || []);
      setRecipients(r.data?.recipients_count || 0);
    } catch (e) {
      console.error("[Novenas] load:", e?.message);
      toast.error("Erro a carregar novenas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-novenas-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[hsl(var(--primary))]">Novenas</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Envio automático diário às 17h00 (hora de Lisboa) para {recipients} sócio(s) aprovados.
            Os sócios podem cancelar a subscrição no rodapé do email.
          </p>
        </div>
        <button onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] font-semibold"
          data-testid="novena-create-btn">
          <Plus className="h-4 w-4" /> Nova novena
        </button>
      </div>

      <div className="mt-8">
        <AliancaCard />
      </div>

      <div className="space-y-5">
        {loading ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Sem novenas criadas.</p>
        ) : (
          items.map((n) => (
            <NovenaCard key={n.id} novena={n} recipientsCount={recipients} onChanged={load} onEdit={(nov) => setEditing(nov)} />
          ))
        )}
      </div>

      {editing !== null && (
        <NovenaForm
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
